// Email-safe markdown → HTML string.
// Mirrors ContractMarkdown.jsx but emits fully inline styles instead of React
// elements + CSS classes, since Outlook strips <style> blocks and class names.
import { escapeHtml } from "./escapeHtml";

export function contractMarkdownToEmailHtml(text) {
  if (!text) return "";

  const renderInline = (str) => {
    // Escape any raw HTML in the source FIRST (see utils/escapeHtml.js — the
    // text derives from attacker-supplied uploaded documents, and this HTML
    // runs in the scripted print popup and in emails). The markdown transforms
    // below then inject only known-safe HTML. Do not reorder: escaping must
    // precede the tag-injecting replaces.
    let s = escapeHtml(str);
    s = s.replace(/✅\s*\*\*COMPLIANT\*\*/g, '<span style="display:inline-block;background:#d1fae5;color:#065f46;font-size:10px;font-weight:700;padding:1px 7px;border-radius:100px;">&#10003; Compliant</span>');
    s = s.replace(/✅\s*\*\*EXCEEDS\*\*/g, '<span style="display:inline-block;background:#dbeafe;color:#1e40af;font-size:10px;font-weight:700;padding:1px 7px;border-radius:100px;">&#8593; Exceeds</span>');
    s = s.replace(/❌\s*\*\*([^*]+)\*\*/g, '<span style="display:inline-block;background:#fee2e2;color:#991b1b;font-size:10px;font-weight:700;padding:1px 7px;border-radius:100px;">&#10007; $1</span>');
    s = s.replace(/⚠️\s*\*\*([^*]+)\*\*/g, '<span style="display:inline-block;background:#fef3c7;color:#92400e;font-size:10px;font-weight:700;padding:1px 7px;border-radius:100px;">&#9888; $1</span>');
    s = s.replace(/🔴\s*/g, '<span style="color:#dc2626;font-size:10px;font-weight:800;">&#9679; HIGH</span> ');
    s = s.replace(/🟡\s*/g, '<span style="color:#d97706;font-size:10px;font-weight:800;">&#9679; MED</span> ');
    s = s.replace(/🟢\s*/g, '<span style="color:#16a34a;font-size:10px;font-weight:800;">&#9679; LOW</span> ');
    s = s.replace(/\[x\]/gi, '<span style="color:#16a34a;font-size:14px;">&#9745;</span>');
    s = s.replace(/\[ \]/g, '<span style="color:#9ca3af;font-size:14px;">&#9744;</span>');
    s = s.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
    s = s.replace(/\*([^*]+)\*/g, "<em>$1</em>");
    s = s.replace(/`([^`]+)`/g, '<code style="background:#f1f5f9;color:#0f172a;padding:1px 5px;border-radius:4px;font-size:11px;font-family:monospace;">$1</code>');
    return s;
  };

  const flushTable = (buf) => {
    if (buf.length < 2) return "";
    const headerCells = buf[0].split("|").map(c => c.trim()).filter(Boolean);
    const rows = buf.slice(2).map(row => row.split("|").map(c => c.trim()).filter(Boolean));
    let html = '<div style="overflow-x:auto;margin-bottom:16px;"><table style="width:100%;border-collapse:collapse;font-size:12px;"><thead><tr style="background:#f0f9ff;">';
    headerCells.forEach(h => {
      html += `<th style="padding:6px 10px;border-bottom:2px solid #bae6fd;text-align:left;font-weight:700;color:#0369a1;white-space:nowrap;">${renderInline(h)}</th>`;
    });
    html += "</tr></thead><tbody>";
    rows.forEach((row, ri) => {
      html += `<tr style="background:${ri % 2 === 0 ? "#fff" : "#f8fafc"};">`;
      row.forEach(cell => {
        html += `<td style="padding:6px 10px;border-bottom:1px solid #e0f2fe;vertical-align:top;">${renderInline(cell)}</td>`;
      });
      html += "</tr>";
    });
    html += "</tbody></table></div>";
    return html;
  };

  const lines = text.split("\n");
  let html = "";
  let tableBuffer = [];
  let inTable = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    if (line.trim().startsWith("|")) {
      inTable = true;
      tableBuffer.push(line.trim());
      continue;
    } else if (inTable) {
      html += flushTable(tableBuffer);
      tableBuffer = [];
      inTable = false;
    }

    const trimmed = line.trim();

    if (trimmed.startsWith("# ")) {
      html += `<h1 style="font-size:16px;font-weight:800;color:#0c4a6e;margin:20px 0 8px;padding-bottom:6px;border-bottom:2px solid #bae6fd;">${renderInline(trimmed.slice(2))}</h1>`;
    } else if (trimmed.startsWith("## ")) {
      const headerText = trimmed.slice(3);
      const isHighCrit = /high|critical/i.test(headerText);
      const isMed = /medium/i.test(headerText);
      const color = isHighCrit ? "#dc2626" : isMed ? "#d97706" : "#0369a1";
      html += `<h2 style="font-size:14px;font-weight:700;color:${color};margin:18px 0 6px;">${renderInline(headerText)}</h2>`;
    } else if (trimmed.startsWith("### ")) {
      html += `<h3 style="font-size:13px;font-weight:700;color:#1e3a5f;margin:14px 0 4px;">${renderInline(trimmed.slice(4))}</h3>`;
    } else if (trimmed.startsWith("#### ")) {
      html += `<h4 style="font-size:12px;font-weight:700;color:#374151;margin:10px 0 3px;">${renderInline(trimmed.slice(5))}</h4>`;
    } else if (trimmed === "---" || trimmed === "***") {
      html += '<hr style="border:none;border-top:1px solid #e0f2fe;margin:16px 0;" />';
    } else if (/^[-*]\s+\[[ x]\]/i.test(trimmed)) {
      const checked = /\[x\]/i.test(trimmed);
      const itemText = trimmed.replace(/^[-*]\s+\[[ x]\]\s*/i, "");
      html += `<div style="margin:3px 0;padding-left:4px;"><span style="font-size:14px;color:${checked ? "#16a34a" : "#9ca3af"};">${checked ? "&#9745;" : "&#9744;"}</span> <span style="font-size:12px;color:#111827;line-height:1.5;">${renderInline(itemText)}</span></div>`;
    } else if (/^[-*•]\s+/.test(trimmed)) {
      const itemText = trimmed.replace(/^[-*•]\s+/, "");
      const isFlag = /^(?:\*\*FLAG|FLAG|ACTION|Recommended Action)/i.test(itemText);
      html += `<div style="margin:3px 0 3px 12px;"><span style="color:${isFlag ? "#dc2626" : "#6b7280"};font-size:10px;">&#9679;</span> <span style="font-size:12px;color:#111827;line-height:1.6;">${renderInline(itemText)}</span></div>`;
    } else if (/^\d+\.\s+/.test(trimmed)) {
      const num = trimmed.match(/^(\d+)\./)[1];
      const itemText = trimmed.replace(/^\d+\.\s+/, "");
      html += `<div style="margin:4px 0 4px 8px;"><span style="font-size:12px;font-weight:700;color:#0369a1;">${num}.</span> <span style="font-size:12px;color:#111827;line-height:1.6;">${renderInline(itemText)}</span></div>`;
    } else if (trimmed.startsWith(">")) {
      html += `<blockquote style="margin:8px 0;padding-left:12px;border-left:3px solid #7dd3fc;color:#374151;font-style:italic;font-size:12px;line-height:1.6;">${renderInline(trimmed.slice(1).trim())}</blockquote>`;
    } else if (!trimmed) {
      html += '<div style="height:6px;line-height:6px;">&nbsp;</div>';
    } else {
      html += `<p style="font-size:12px;color:#111827;line-height:1.7;margin:4px 0;">${renderInline(trimmed)}</p>`;
    }
  }

  if (inTable && tableBuffer.length > 0) {
    html += flushTable(tableBuffer);
  }

  return html;
}
